import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PWAProvider } from "@/context/PWAContext";
import { Toaster } from "@/components/ui/sonner";
import FullScreenLoader from "@/components/FullScreenLoader";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Dashboard from "@/pages/dashboard/Dashboard";
import Products from "@/pages/dashboard/Products";
import ProductEditor from "@/pages/dashboard/ProductEditor";
import Inventory from "@/pages/dashboard/Inventory";
import Customers from "@/pages/dashboard/Customers";
import CustomerDetail from "@/pages/dashboard/CustomerDetail";
import Orders from "@/pages/dashboard/Orders";
import OrderDetail from "@/pages/dashboard/OrderDetail";
import BatchPlanner from "@/pages/dashboard/BatchPlanner";
import CreateOrder from "@/pages/dashboard/CreateOrder";
import PriceSheets from "@/pages/dashboard/PriceSheets";
import PriceSheetEditor from "@/pages/dashboard/PriceSheetEditor";
import Reports from "@/pages/dashboard/Reports";
import Settings from "@/pages/dashboard/Settings";

import PlatformLayout from "@/pages/platform/PlatformLayout";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformStorefronts from "@/pages/platform/PlatformStorefronts";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import PlatformLegal from "@/pages/platform/PlatformLegal";

import StoreLayout from "@/pages/store/StoreLayout";
import Storefront from "@/pages/store/Storefront";
import StoreProduct from "@/pages/store/StoreProduct";
import Cart from "@/pages/store/Cart";
import Checkout from "@/pages/store/Checkout";
import CustomerAuth from "@/pages/store/CustomerAuth";
import CustomerOrders from "@/pages/store/CustomerOrders";
import CustomerOrderDetail from "@/pages/store/CustomerOrderDetail";
import CustomerAccount from "@/pages/store/CustomerAccount";

function Guard({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "staff" && !["owner", "staff"].includes(user.tenant_role)) return <Navigate to="/login" replace />;
  if (role === "admin" && user.platform_role !== "admin") return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PWAProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/legal/:docType" element={<LegalPage />} />

            <Route path="/dashboard" element={<Guard role="staff"><DashboardLayout /></Guard>}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<ProductEditor />} />
              <Route path="products/:pid" element={<ProductEditor />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:cid" element={<CustomerDetail />} />
              <Route path="price-sheets" element={<PriceSheets />} />
              <Route path="price-sheets/:sid" element={<PriceSheetEditor />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/new" element={<CreateOrder />} />
              <Route path="orders/plan" element={<BatchPlanner />} />
              <Route path="orders/:oid" element={<OrderDetail />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/platform" element={<Guard role="admin"><PlatformLayout /></Guard>}>
              <Route index element={<PlatformDashboard />} />
              <Route path="storefronts" element={<PlatformStorefronts />} />
              <Route path="users" element={<PlatformUsers />} />
              <Route path="legal" element={<PlatformLegal />} />
            </Route>

            <Route path="/store/:slug" element={<StoreLayout />}>
              <Route index element={<Storefront />} />
              <Route path="product/:pid" element={<StoreProduct />} />
              <Route path="cart" element={<Cart />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="login" element={<CustomerAuth />} />
              <Route path="register" element={<CustomerAuth register />} />
              <Route path="account" element={<CustomerAccount />} />
              <Route path="orders" element={<CustomerOrders />} />
              <Route path="orders/:oid" element={<CustomerOrderDetail />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </PWAProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
